import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Paper,
    Avatar
} from '@mui/material';
import {
    Settings as SettingsIcon,
    MonetizationOn as CurrencyIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';

const SettingsPage = () => {
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, formState: { errors, isDirty } } = useForm({
        defaultValues: {
            currency: user?.settings?.currency || 'USD'
        }
    });

    const currencyOptions = [
        { value: 'USD', label: 'US Dollar ($)', symbol: '$' },
        { value: 'GBP', label: 'British Pound (£)', symbol: '£' },
        { value: 'PKR', label: 'Pakistani Rupee (PKR)', symbol: 'PKR' },
        { value: 'INR', label: 'Indian Rupee (₹)', symbol: '₹' }
    ];

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            const response = await authAPI.updateSettings(data);

            // Update user in context
            updateUser(response.data.user);

            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error(error.response?.data?.message || 'Failed to update settings');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Settings
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage your account preferences and display settings
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Currency Settings */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={3}>
                                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                    <CurrencyIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Currency Settings
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Choose your preferred currency for displaying prices throughout the dashboard
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="currency"
                                            control={control}
                                            rules={{ required: 'Currency selection is required' }}
                                            render={({ field }) => (
                                                <FormControl fullWidth error={!!errors.currency}>
                                                    <InputLabel>Preferred Currency</InputLabel>
                                                    <Select
                                                        {...field}
                                                        label="Preferred Currency"
                                                    >
                                                        {currencyOptions.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Typography fontWeight="bold">
                                                                        {option.symbol}
                                                                    </Typography>
                                                                    <Typography>
                                                                        {option.label}
                                                                    </Typography>
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors.currency && (
                                                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                                            {errors.currency.message}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            <Typography variant="body2">
                                                <strong>Note:</strong> Changing the currency will only update the display symbol throughout the dashboard.
                                                No currency conversion will be performed - only the symbol will change.
                                            </Typography>
                                        </Alert>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box display="flex" gap={2}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                                                disabled={loading || !isDirty}
                                            >
                                                {loading ? 'Saving...' : 'Save Settings'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Current Settings Summary */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                                    <SettingsIcon />
                                </Avatar>
                                <Typography variant="h6">
                                    Current Settings
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Display Currency
                                </Typography>
                                <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="h6" color="primary">
                                            {currencyOptions.find(c => c.value === (user?.settings?.currency || 'USD'))?.symbol}
                                        </Typography>
                                        <Typography variant="body1">
                                            {currencyOptions.find(c => c.value === (user?.settings?.currency || 'USD'))?.label}
                                        </Typography>
                                    </Box>
                                </Paper>

                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Account Information
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="body2" gutterBottom>
                                        <strong>Name:</strong> {user?.firstName} {user?.lastName}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        <strong>Email:</strong> {user?.email}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Role:</strong> {user?.role}
                                    </Typography>
                                </Paper>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SettingsPage;
